/**
 * Admin Support View
 *
 * Support inbox for administrators to view and manage user feedback:
 * - Filterable by user and issue type (bug/feature/question)
 * - Table with feedback submissions
 * - Detail modal with full feedback content
 * - Screenshot viewing with enlarge capability
 */

import { useState, useMemo } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { Users, X } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useFeedbackData } from "../hooks/useAdminData";
import {
  FeedbackItem,
  HOURGLASS_INTERNAL_EMAILS,
  getFeedbackSummary,
  parseScreenshots,
} from "../utils/adminHelpers";
import styles from "./AdminViews.module.css";

export default function AdminSupportView() {
  const { user } = useAuth();
  const currentUserEmail = user?.email || "";

  // ─────────────────────────────────────────────
  // Data fetching
  // ─────────────────────────────────────────────
  const { feedbackData, loading: loadingFeedback } = useFeedbackData();

  // ─────────────────────────────────────────────
  // Filter state
  // ─────────────────────────────────────────────
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState<string>("all");
  const [feedbackSelectedEmails, setFeedbackSelectedEmails] = useState<
    string[]
  >([]);

  // ─────────────────────────────────────────────
  // Modal state
  // ─────────────────────────────────────────────
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(
    null
  );
  const [enlargedScreenshot, setEnlargedScreenshot] = useState<string | null>(
    null
  );

  // ─────────────────────────────────────────────
  // Computed data
  // ─────────────────────────────────────────────
  // Extract unique users from feedback data
  const feedbackUsers = useMemo(() => {
    const usersMap = new Map<string, { email: string; name: string }>();
    feedbackData.forEach((item) => {
      // Skip internal emails unless current user is internal
      if (
        !HOURGLASS_INTERNAL_EMAILS.includes(currentUserEmail) &&
        HOURGLASS_INTERNAL_EMAILS.includes(item.user_email)
      ) {
        return;
      }
      if (!usersMap.has(item.user_email)) {
        usersMap.set(item.user_email, {
          email: item.user_email,
          name: item.user_name,
        });
      }
    });
    return Array.from(usersMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [feedbackData, currentUserEmail]);

  // Filter feedback data
  const filteredFeedbackData = useMemo(() => {
    let filtered = feedbackData;

    // Hide internal Hourglass emails unless the current user is one of them
    if (!HOURGLASS_INTERNAL_EMAILS.includes(currentUserEmail)) {
      filtered = filtered.filter(
        (item) => !HOURGLASS_INTERNAL_EMAILS.includes(item.user_email)
      );
    }

    // Filter by type
    if (feedbackTypeFilter !== "all") {
      filtered = filtered.filter(
        (item) => item.issue_type === feedbackTypeFilter
      );
    }

    // Filter by selected users
    if (feedbackSelectedEmails.length > 0) {
      filtered = filtered.filter((item) =>
        feedbackSelectedEmails.includes(item.user_email)
      );
    }

    return filtered;
  }, [feedbackData, feedbackTypeFilter, feedbackSelectedEmails, currentUserEmail]);

  // ─────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────
  const getBadgeClass = (issueType: string) => {
    switch (issueType) {
      case "bug":
        return styles.badgeBug;
      case "feature":
        return styles.badgeFeature;
      case "question":
        return styles.badgeQuestion;
      default:
        return "";
    }
  };

  return (
    <div className={styles.adminViewWrapper}>
      {/* Feedback Detail Modal */}
      {selectedFeedback && (
        <div
          className={styles.modalOverlay}
          onClick={() => setSelectedFeedback(null)}
        >
          <div
            className={cn(styles.modal, "max-w-2xl")}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedFeedback.issue_type === "bug" && "Bug Report"}
                  {selectedFeedback.issue_type === "feature" && "Feature Request"}
                  {selectedFeedback.issue_type === "question" && "Question"}
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    #{selectedFeedback.id}
                  </span>
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedFeedback.user_name} ({selectedFeedback.user_email})
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(selectedFeedback.created_at).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => setSelectedFeedback(null)}
                className={styles.modalCloseButton}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {selectedFeedback.issue_type === "bug" && (
                <>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                      What happened?
                    </h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded">
                      {selectedFeedback.what_happened || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                      Expected behavior
                    </h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded">
                      {selectedFeedback.expected_behavior || "Not provided"}
                    </p>
                  </div>
                  {selectedFeedback.bug_anything_else && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">
                        Additional context
                      </h4>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded">
                        {selectedFeedback.bug_anything_else}
                      </p>
                    </div>
                  )}
                </>
              )}

              {selectedFeedback.issue_type === "feature" && (
                <>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                      Feature description
                    </h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded">
                      {selectedFeedback.feature_description || "Not provided"}
                    </p>
                  </div>
                  {selectedFeedback.current_workaround && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">
                        How it would help
                      </h4>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded">
                        {selectedFeedback.current_workaround}
                      </p>
                    </div>
                  )}
                  {selectedFeedback.feature_anything_else && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">
                        Additional context
                      </h4>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded">
                        {selectedFeedback.feature_anything_else}
                      </p>
                    </div>
                  )}
                </>
              )}

              {selectedFeedback.issue_type === "question" && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">
                    Question
                  </h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded">
                    {selectedFeedback.question || "Not provided"}
                  </p>
                </div>
              )}

              {/* Screenshots Gallery in Modal */}
              {(() => {
                const screenshots = parseScreenshots(selectedFeedback.screenshots);
                if (screenshots.length === 0) return null;
                return (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Screenshots ({screenshots.length})
                    </h4>
                    <div className={styles.screenshotGallery}>
                      {screenshots.map((src, idx) => (
                        <img
                          key={idx}
                          src={src}
                          alt={`Screenshot ${idx + 1}`}
                          className={styles.screenshotLarge}
                          onClick={() => setEnlargedScreenshot(src)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="mt-6 flex justify-end">
              <Button variant="outline" onClick={() => setSelectedFeedback(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Enlarged Screenshot Modal */}
      {enlargedScreenshot && (
        <div
          className={styles.screenshotModalOverlay}
          onClick={() => setEnlargedScreenshot(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button
              onClick={() => setEnlargedScreenshot(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="h-8 w-8" />
            </button>
            <img
              src={enlargedScreenshot}
              alt="Enlarged screenshot"
              className={styles.screenshotEnlarged}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      <div className={styles.settingsSectionFullWidth}>
        {/* Header with filters */}
        <div className={styles.filterHeader}>
          <h2 className={styles.metricsHeader}>Support Inbox</h2>
          <div className={styles.filterControls}>
            {/* User Filter Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[180px] justify-start text-left font-normal h-10"
                >
                  <Users className="mr-2 h-4 w-4 flex-shrink-0" />
                  {feedbackSelectedEmails.length === 0 ? (
                    "All Users"
                  ) : feedbackSelectedEmails.length <= 2 ? (
                    <span className="truncate">
                      {feedbackUsers
                        .filter((u) => feedbackSelectedEmails.includes(u.email))
                        .map((u) => u.name.split(" ")[0])
                        .join(", ")}
                    </span>
                  ) : (
                    `${feedbackSelectedEmails.length} selected`
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="end">
                <div className="p-2 border-b flex items-center justify-between">
                  <span className="text-sm font-medium">Filter Users</span>
                  <div className="flex gap-1">
                    {feedbackSelectedEmails.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFeedbackSelectedEmails([])}
                        className="h-7 text-xs"
                      >
                        Clear
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (
                          feedbackSelectedEmails.length === feedbackUsers.length
                        ) {
                          setFeedbackSelectedEmails([]);
                        } else {
                          setFeedbackSelectedEmails(
                            feedbackUsers.map((u) => u.email)
                          );
                        }
                      }}
                      className="h-7 text-xs"
                    >
                      {feedbackSelectedEmails.length === feedbackUsers.length
                        ? "None"
                        : "All"}
                    </Button>
                  </div>
                </div>
                <div className="max-h-[200px] overflow-y-auto p-2">
                  {feedbackUsers.map((user) => (
                    <label
                      key={user.email}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={feedbackSelectedEmails.includes(user.email)}
                        onChange={() => {
                          if (feedbackSelectedEmails.includes(user.email)) {
                            setFeedbackSelectedEmails(
                              feedbackSelectedEmails.filter(
                                (e) => e !== user.email
                              )
                            );
                          } else {
                            setFeedbackSelectedEmails([
                              ...feedbackSelectedEmails,
                              user.email,
                            ]);
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 flex-shrink-0"
                      />
                      <span className="text-sm">{user.name}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Type Filter Dropdown */}
            <Select
              value={feedbackTypeFilter}
              onValueChange={setFeedbackTypeFilter}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="bottom" align="end">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="bug">Bug Reports</SelectItem>
                <SelectItem value="feature">Feature Requests</SelectItem>
                <SelectItem value="question">Questions</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className={styles.settingsSectionContent}>
          {loadingFeedback ? (
            <p className={styles.loadingText}>Loading feedback...</p>
          ) : filteredFeedbackData.length === 0 ? (
            <p className={styles.emptyText}>No feedback submissions found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-border bg-secondary/30">
                    <th className="text-left p-2 font-medium w-16">ID</th>
                    <th className="text-left p-2 font-medium w-36">Submitted</th>
                    <th className="text-left p-2 font-medium w-32">Name</th>
                    <th className="text-left p-2 font-medium w-48">Email</th>
                    <th className="text-left p-2 font-medium w-24">Type</th>
                    <th className="text-left p-2 font-medium">Summary</th>
                    <th className="text-left p-2 font-medium w-32">
                      Screenshots
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeedbackData.map((item, index) => {
                    const screenshots = parseScreenshots(item.screenshots);
                    return (
                      <tr
                        key={item.id}
                        className={cn(
                          "border-b hover:bg-secondary/50 dark:hover:bg-neutral-500/30 transition-colors cursor-pointer",
                          index % 2 === 1 && "bg-secondary/20"
                        )}
                        onClick={() => setSelectedFeedback(item)}
                      >
                        <td className="p-2 font-mono text-xs">{item.id}</td>
                        <td className="p-2 text-xs text-muted-foreground">
                          {new Date(item.created_at).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="p-2 font-medium">{item.user_name}</td>
                        <td className="p-2 text-xs text-muted-foreground">
                          {item.user_email}
                        </td>
                        <td className="p-2">
                          <span
                            className={cn(styles.badge, getBadgeClass(item.issue_type))}
                          >
                            {item.issue_type.charAt(0).toUpperCase() +
                              item.issue_type.slice(1)}
                          </span>
                        </td>
                        <td className="p-2 max-w-[300px]">
                          <p className="truncate text-sm">
                            {getFeedbackSummary(item)}
                          </p>
                        </td>
                        <td className="p-2">
                          {screenshots.length > 0 ? (
                            <div className="flex gap-1 overflow-x-auto max-w-[120px]">
                              {screenshots.slice(0, 3).map((src, idx) => (
                                <img
                                  key={idx}
                                  src={src}
                                  alt={`Screenshot ${idx + 1}`}
                                  className={styles.screenshotThumbnail}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEnlargedScreenshot(src);
                                  }}
                                />
                              ))}
                              {screenshots.length > 3 && (
                                <span className="text-xs text-muted-foreground flex items-center">
                                  +{screenshots.length - 3}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              None
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
